import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from '../../config';
import { jwtDecode } from 'jwt-decode';
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS";
import jobstyles from "../JobDashboard/NewHomeUserJobsDashboard.module.css";
import styles from "./ModernSubscription.module.css";

const ModernSubscriptionPage = () => {
  const [username, setUsername] = useState("");

  // Subscription Types (plans + PAYG options)
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [advertisingOptions, setAdvertisingOptions] = useState([]);

  // Loading and errors for subscription types (pricing grid)
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard: Active subscription(s) and credits
  const [activeSubs, setActiveSubs] = useState([]);
  const [subLoading, setSubLoading] = useState(true);

  const [credits, setCredits] = useState({ socialCredits: 0, videoCredits: 0 });
  const [creditLoading, setCreditLoading] = useState(true);

  // One-time purchase button loading
  const [actionLoading, setActionLoading] = useState(false);

  // Helpers
  const precedence = (id) => (id === 3 ? 3 : id === 2 ? 2 : id === 1 ? 1 : 0);
  const getTypeId = (s) => s?.type?.subscriptionTypeId ?? s?.subscriptionTypeId;

  const formatDate = (d) => {
    if (!d) return "";
    try {
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return d;
      return dt.toLocaleDateString();
    } catch {
      return d;
    }
  };

  const creditText = (val) => {
    if (val === -1) return "Unlimited";
    return String(val || 0);
  };

  // Fetch: Subscription Types (Plans + PAYG)
  const fetchSubscriptionTypes = async (businessId) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/Subscription/getSubscriptionTypes`, {
        params: { mtype: "LISTING", bizid: businessId },
      });
      const data = response.data || [];
      setSubscriptions(data);
    } catch (err) {
      console.error("Error fetching subscriptions: ", err);
      setError("Failed to load subscriptions. Please try again later.");
      setSubscriptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch: Active Subscription(s) for dashboard details
  const fetchActiveSubscription = async (token) => {
    setSubLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/Subscription/getActiveSubscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const arr = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
      setActiveSubs(arr);
    } catch (e) {
      console.error("Error fetching active subscription(s):", e);
      setActiveSubs([]);
    } finally {
      setSubLoading(false);
    }
  };

  // Fetch: Credits for Pro plans (type 2/3)
  const fetchSubscriptionCredits = async (token) => {
    setCreditLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/Subscription/getCredits`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const snap = {
        socialCredits: typeof res.data?.socialCredits === "number" ? res.data.socialCredits : 0,
        videoCredits: typeof res.data?.videoCredits === "number" ? res.data.videoCredits : 0,
      };
      setCredits(snap);
    } catch (e) {
      console.error("Error fetching credits:", e);
      setCredits({ socialCredits: 0, videoCredits: 0 });
    } finally {
      setCreditLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const bootstrap = async () => {
      if (token) {
        const decodeToken = jwtDecode(token);
        setUsername(decodeToken.UserName);
        const businessId = decodeToken.BusinessId;

        // Load pricing data (plans + PAYG)
        await fetchSubscriptionTypes(businessId);

        // Load dashboard data (active subs + credits)
        fetchActiveSubscription(token);
        fetchSubscriptionCredits(token);
      } else {
        // No token, stop spinners
        setIsLoading(false);
        setSubLoading(false);
        setCreditLoading(false);
      }
    };
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Split subscription types into plans vs PAYG
  useEffect(() => {
    if (Array.isArray(subscriptions)) {
      const plans = subscriptions.filter((sub) => !sub.title?.toLowerCase().includes(" ad"));
      const ads = subscriptions.filter((sub) => sub.title?.toLowerCase().includes(" ad"));
      setSubscriptionPlans(plans);
      setAdvertisingOptions(ads);
    }
  }, [subscriptions]);

  // Derived: choose the primary active subscription (3 > 2 > 1)
  const coreActive = activeSubs.filter((s) => {
    const id = getTypeId(s);
    return id === 1 || id === 2 || id === 3;
  });
  const chosenSub = coreActive
    .slice()
    .sort((a, b) => {
      const pb = precedence(getTypeId(b));
      const pa = precedence(getTypeId(a));
      if (pb !== pa) return pb - pa;
      const db = b?.datePaid ? new Date(b.datePaid).getTime() : 0;
      const da = a?.datePaid ? new Date(a.datePaid).getTime() : 0;
      return db - da;
    })[0];
  const chosenTypeId = chosenSub ? getTypeId(chosenSub) : null;
  const fallbackChosenTypeId = !chosenTypeId ? (subscriptionPlans.find(p => p?.hasValidSubscription)?.subscriptionTypeId ?? null) : null;
  const effectiveChosenTypeId = chosenTypeId || fallbackChosenTypeId;
  const hasActive = !!effectiveChosenTypeId;
  const shouldShowPAYG = !hasActive || effectiveChosenTypeId === 1;
  const showCreditsOnly = effectiveChosenTypeId === 2 || effectiveChosenTypeId === 3;
  const showCreditsStarter = effectiveChosenTypeId === 1;

  // Disable lower-tier plan purchases when a higher-tier is active
  // Rules:
  // - If active type = 3, disable purchase for type 2 and 1
  // - If active type = 2, disable purchase for type 1
  // - If active type = 1 or no active, do not disable any plan (beyond hasValidSubscription)
  const isPlanDisabled = (plan) => {
    const id = plan?.subscriptionTypeId;
    const eff = effectiveChosenTypeId;
    console.log('[Subscription] isPlanDisabled', { planId: id, effectiveChosenTypeId: eff });
    if (!id) return false;
    if (eff === 3) return id === 2 || id === 1;
    if (eff === 2) return id === 1;
    return false;
  };

  // Purchase subscription
  const handlePurchase = async (title, stripeProductId, mySubscriptionTypeId) => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const decodeToken = jwtDecode(token);
        const businessId = decodeToken.BusinessId;
        const response = await axios.post(`${API_BASE_URL}/api/StripeWebhook/create-checkout-session`, {
          priceId: stripeProductId,
          businessId: businessId,
          successUrl: `${window.location.origin}/subscription/success`,
          cancelUrl: `${window.location.origin}/subscription/cancel`,
          customerEmail: decodeToken.unique_name,
          subscriptionTypeId: mySubscriptionTypeId,
          title: title,
          mode: "subscription",
        });
        const { url } = response.data || {};
        if (url) window.location.href = url;
      }
    } catch (error) {
      console.error("Error creating checkout session: ", error);
    }
  };

  // One-time purchase (PAYG)
  const handleOneTimePurchase = async (title, stripeProductId, productTypeId) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) return;

      const decodeToken = jwtDecode(token);
      const businessId = decodeToken?.BusinessId;
      const customerEmail = decodeToken?.unique_name || decodeToken?.email || "";

      const response = await axios.post(`${API_BASE_URL}/api/StripeWebhook/create-checkout-session`, {
        priceId: stripeProductId,
        businessId: businessId,
        successUrl: `${window.location.origin}/subscription/success`,
        cancelUrl: `${window.location.origin}/subscription/cancel`,
        customerEmail: customerEmail,
        subscriptionTypeId: productTypeId, // one-time product id
        title: title,
        mode: "payment",
      });

      if (response?.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating one-time payment session: ", error);
    } finally {
      setActionLoading(false);
    }
  };

  // Render pricing grid + conditional PAYG section
  const renderContent = () => {
    if (isLoading) {
      return <div className={styles.loadingMessage}>Loading Subscriptions...</div>;
    }

    if (error) {
      return <div className={styles.errorMessage}>{error}</div>;
    }

    if (subscriptionPlans.length === 0 && advertisingOptions.length === 0) {
      return <div className={styles.loadingMessage}>No subscription plans available at this time.</div>;
    }

    return (
      <>
        {/* Subscription Plans */}
        <div className={styles.pricingGrid}>
          {subscriptionPlans.map((plan, index) => {
            const isFeatured = plan.title === "Pro Pack";
            const disabledByTier = isPlanDisabled(plan);
            console.log('[Subscription] Plan disable check', { planId: plan?.subscriptionTypeId, effectiveChosenTypeId, disabledByTier, hasValid: plan?.hasValidSubscription });
            return (
              <div key={index} className={`${styles.pricingCard} ${isFeatured ? styles.featuredPlan : ""}`}>
                {isFeatured && <span className={styles.popularBadge}>Most Popular</span>}
                <h3>{plan.title}</h3>
                <p className={styles.price}>${plan.price}/mo</p>
                <ul>
                  {Array.isArray(plan.features) &&
                    plan.features.map((feature, fIndex) => (
                      <li key={fIndex}>✓ {feature.featureName}</li>
                    ))}
                </ul>
                <button
                  onClick={() =>
                    handlePurchase(plan.title, plan.stripeProductId, plan.subscriptionTypeId)
                  }
                  className={isFeatured ? styles.btnPrimary : styles.btnSecondary}
                  disabled={plan.hasValidSubscription || disabledByTier}
                  title={
                    plan.hasValidSubscription
                      ? "You are already subscribed to this plan"
                      : disabledByTier
                      ? "A higher tier subscription is active"
                      : "Purchase this plan"
                  }
                >
                  {plan.hasValidSubscription ? "Currently Subscribed" : "Purchase Plan"}
                </button>
              </div>
            );
          })}
        </div>

        {/* PAY AS YOU GO (only when no active sub OR active is type=1) */}
        {shouldShowPAYG && advertisingOptions.length > 0 && (
          <div className={styles.payAsYouGoSection}>
            <h2 className={styles.sectionTitle}>Pay as you go</h2>
            <p className={styles.sectionSubtitle}>
              Boost your visibility with a one-time purchase. Ideal for promoting a specific event or project.
            </p>
            <div className={styles.optionsGrid}>
              {advertisingOptions.map((option) => (
                <div key={option.id} className={styles.optionCard}>
                  <div className={styles.optionDetails}>
                    <h3 className={styles.optionTitle}>{option.title}</h3>
                    <p className={styles.optionDescription}>{option.description}</p>
                  </div>
                  <div className={styles.optionAction}>
                    <p className={styles.optionPrice}>${option.price}</p>
                    <button
                      onClick={() =>
                        handleOneTimePurchase(option.title, option.stripeProductId, option.id)
                      }
                      className={styles.purchaseButton}
                      disabled={actionLoading}
                    >
                      {actionLoading ? "Processing..." : "Purchase"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={
        jobstyles.jobDashboard_minHeightScreen +
        " " +
        jobstyles.jobDashboard_bgGradientBlueAmber
      }
    >
      <ModernNavbarPureCSS UserName={username} prefix={""} TabActiveId={"subscription"} />

      {/* Consistent Header */}
      <header
        className={`${jobstyles.jobDashboard_bgGradientHeader} ${jobstyles.jobDashboard_header}`}
        style={{ position: "sticky", top: 0, zIndex: 1020 }}
      >
        <div className={jobstyles.bizjobDashboard_headerContent}>
          <div className={jobstyles.jobDashboard_headerLeft}>
            <div className={jobstyles.jobDashboard_headerLogo}>
              {/* Optional logo/icon area */}
              <span className={jobstyles.jobDashboard_headerTitle}>Subscriptions</span>
            </div>
            <div className={jobstyles.jobDashboard_headerSubtitle}>
              Manage your plans, purchases, and ad credits
            </div>
          </div>
          <div className={jobstyles.jobDashboard_headerRight}>
            <span
              className={`${jobstyles.jobDashboard_badge} ${jobstyles.jobDashboard_badgeSecondary}`}
              title="Subscription Dashboard"
            >
              Dashboard
            </span>
          </div>
        </div>
      </header>

      {/* Dashboard Subscription Details */}
      <section className={styles.pricingSection}>
        <div className={jobstyles.jobDashboard_container}>
          <div
            className={`${jobstyles.jobDashboard_grid} ${jobstyles.jobDashboard_gap6} ${jobstyles.jobDashboard_md_gridCols2}`}
          >
            {/* Active Subscription Card */}
            <div className={jobstyles.jobDashboard_card}>
              <div className={jobstyles.jobDashboard_cardHeader}>
                <div className={jobstyles.jobDashboard_cardTitle}>Active Subscriptions</div>
              </div>
              <div className={jobstyles.jobDashboard_cardContent}>
                {subLoading ? (
                  <div className={styles.loadingMessage}>Loading subscriptions...</div>
                ) : !hasActive ? (
                  <div className={styles.loadingMessage}>
                    No active subscription found. Choose a plan or use pay-as-you-go options.
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="rounded-xl" style={{ background: "#f9fafb", padding: "1rem", border: "1px solid #e5e7eb", borderRadius: "0.75rem" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "0.75rem" }}>
                        <div style={{ minWidth: 220 }}>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>Plan</div>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {chosenSub?.type?.title || "—"}{" "}
                            <span style={{ fontSize: 12, color: "#6b7280" }}>
                              (Type ID: {getTypeId(chosenSub) ?? "—"})
                            </span>
                          </div>
                          <div style={{ fontSize: 14, color: "#4b5563", marginTop: 4 }}>
                            {chosenSub?.type?.description || ""}
                          </div>
                        </div>

                        <div style={{ minWidth: 160 }}>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>Price</div>
                          <div style={{ fontSize: 18, fontWeight: 600, color: 'green' }}>
                            {chosenSub?.type?.price != null
                              ? `$${Number(chosenSub.type.price).toFixed(2)}`
                              : "—"}
                          </div>
                          <div style={{ fontSize: 14, color: "#4b5563", marginTop: 4 }}>
                            {chosenSub?.type?.terms || ""}
                          </div>
                        </div>

                        <div style={{ minWidth: 180 }}>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>Paid</div>
                          <div style={{ fontSize: 18, fontWeight: 600, color: 'green' }}>
                            {chosenSub?.amountPaid != null
                              ? `$${Number(chosenSub.amountPaid).toFixed(2)}`
                              : "—"}
                          </div>
                          <div style={{ fontSize: 14, color: "#4b5563", marginTop: 4 }}>
                            Date: {formatDate(chosenSub?.datePaid)}
                          </div>
                        </div>

                        <div style={{ minWidth: 160 }}>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>Expiry</div>
                          <div style={{ fontSize: 18, fontWeight: 600, color: 'green' }}>
                            {formatDate(chosenSub?.expiry)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Starter Ad Credits with purchase (type 1) */}
            {showCreditsStarter && (
              <div className={jobstyles.jobDashboard_card}>
                <div className={jobstyles.jobDashboard_cardHeader}>
                  <div className={jobstyles.jobDashboard_cardTitle}>Build Start Social Platform Ad Credits</div>
                </div>
                <div className={jobstyles.jobDashboard_cardContent}>
                  {creditLoading ? (
                    <div className={styles.loadingMessage}>Loading credits...</div>
                  ) : (
                    <div className={`${jobstyles.jobDashboard_grid} ${jobstyles.jobDashboard_gridCols2} ${jobstyles.jobDashboard_gap4}`}>
                      <div className={jobstyles.jobDashboard_bgGray50} style={{ borderRadius: 12, padding: "1rem" }}>
                        <div style={{ fontSize: 14, color: "#6b7280" }}>Ad Credit for Social Posts</div>
                        <div style={{ fontSize: 20, fontWeight: 600, marginTop: 5 }}>{creditText(credits.socialCredits)}</div>
                      </div>
                      <div className={jobstyles.jobDashboard_bgGray50} style={{ borderRadius: 12, padding: "1rem" }}>
                        <div style={{ fontSize: 14, color: "#6b7280" }}>Ad Credit for Video Posts</div>
                        <div style={{ fontSize: 20, fontWeight: 600, marginTop: 5 }}>{creditText(credits.videoCredits)}</div>
                      </div>
                      <div style={{ gridColumn: "1 / -1", display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.5rem" }}>
                        <button
                          className={`${jobstyles.jobDashboard_btn} ${jobstyles.jobDashboard_btnPrimary}`}
                          disabled={actionLoading}
                          onClick={() => handleOneTimePurchase('Social Ad Post','price_1SFTn6G80LX4ZpjihdOQAFMV', 4)}
                          title="Purchase 1 Social Ad Post credit"
                        >
                          {actionLoading ? "Processing..." : "Purchase Social Ad Post"}
                        </button>
                        <button
                          className={`${jobstyles.jobDashboard_btn} ${jobstyles.jobDashboard_btnPrimary}`}
                          disabled={actionLoading}
                          onClick={() => handleOneTimePurchase('Social Video Ad','price_1SFTnUG80LX4ZpjiXdvIiz85', 5)}
                          title="Purchase 1 Social Video Post credit"
                        >
                          {actionLoading ? "Processing..." : "Purchase Social Video Post"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Ad Credits Card (only for type 2 or 3; no purchase buttons) */}
            {showCreditsOnly && (
              <div className={jobstyles.jobDashboard_card}>
                <div className={jobstyles.jobDashboard_cardHeader}>
                  <div className={jobstyles.jobDashboard_cardTitle}>Suild Start Social Platform Ad Credits</div>
                </div>
                <div className={jobstyles.jobDashboard_cardContent}>
                  {creditLoading ? (
                    <div className={styles.loadingMessage}>Loading credits...</div>
                  ) : (
                    <div className={`${jobstyles.jobDashboard_grid} ${jobstyles.jobDashboard_gridCols2} ${jobstyles.jobDashboard_gap4}`}>
                      
                      <div style={{ gridColumn: "1 / -1", fontSize: 14, color: "#6b7280" }}>
                        Ads in the form of Sponsored Social Posts or Sponsored Social Video Posts will be shown on our Build Start Social Platform. Pro or Corportate plans come with Ad credits.
                      </div>                      
                      
                      <div className={jobstyles.jobDashboard_bgGray50} style={{ borderRadius: 12, padding: "1rem" }}>
                        <div style={{ fontSize: 14, color: "#6b7280" }}>Ad Credit for Social Posts</div>
                        <div style={{ fontSize: 20, fontWeight: 600, marginTop: 5, color: 'green' }}>{creditText(credits.socialCredits)}</div>
                      </div>
                      <div className={jobstyles.jobDashboard_bgGray50} style={{ borderRadius: 12, padding: "1rem" }}>
                        <div style={{ fontSize: 14, color: "#6b7280" }}>Ad Credit for Video Posts</div>
                        <div style={{ fontSize: 20, fontWeight: 600, marginTop: 5, color: 'green' }}>{creditText(credits.videoCredits)}</div>
                      </div>
                      <div style={{ gridColumn: "1 / -1", fontSize: 14, color: "#6b7280" }}>
                        If you are on a Starter plan, you can purchase Ad credits below.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Pricing + conditional PAYG */}
      <section id="pricing" className={styles.pricingSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Plans Designed for Growth</h2>
          <p className={styles.subheadline}>
            Choose a subscription that scales with your business. No hidden fees. Cancel anytime.
          </p>
          {renderContent()}
        </div>
      </section>
    </div>
  );
};

export default ModernSubscriptionPage;
